/**
 * overseerSync.ts — m31 P2 (O2): the decision layer behind <OverseerBridge/> in App.tsx.
 *
 * Kept out of the component so it can be driven (and tested) without a DOM: call syncOverseer() on every auth change.
 * It waits for auth to load, asks the pure gate (utils/overseerGate.ts) whether this session may be recorded, then
 * either starts the SDK and identifies the user or mutes the session. It is idempotent — the services below only act
 * on a change — so calling it again for the same state is harmless.
 *
 * Deliberately NOT part of services/overseer.ts: the gate imports analytics.ts (for the shared bot/QA rules) and
 * analytics.ts imports overseer.ts, so composing the gate inside overseer.ts would close an import cycle.
 *
 * import.meta-free (jest loads it).
 */
import type { JwtPayload } from '@siteforge/shared';
import { shouldInitOverseer, type OverseerGateInput } from '../utils/overseerGate';
import { installOverseerClicks, removeOverseerClicks } from '../utils/overseerClicks';
import { identifyOverseer, initOverseer, muteOverseerForStaff } from './overseer';

export interface OverseerAuthState {
  /** useAuth().loading */
  loading: boolean;
  user: JwtPayload | null;
  /** useAuth().isImpersonating */
  impersonating: boolean;
}

type GateEnvironment = Pick<OverseerGateInput, 'pathname' | 'search' | 'cookies' | 'userAgent' | 'qaSession'>;

function attempt<T>(read: () => T, fallback: T): T {
  try {
    return read();
  } catch {
    return fallback;
  }
}

/** The page-level facts the gate needs, read defensively (any of them can throw in a locked-down browser). */
export function readGateEnvironment(): GateEnvironment {
  return {
    pathname: attempt(() => window.location.pathname, ''),
    search: attempt(() => window.location.search, ''),
    cookies: attempt(() => document.cookie, ''),
    userAgent: attempt(() => navigator.userAgent, ''),
    qaSession: attempt(() => sessionStorage.getItem('sf_qa_session') === '1', false),
  };
}

export function syncOverseer(auth: OverseerAuthState, env: GateEnvironment = readGateEnvironment()): void {
  try {
    if (auth.loading) return; // the gate needs to know who this is

    const allowed = shouldInitOverseer({
      user: auth.user,
      impersonating: auth.impersonating,
      email: auth.user?.email,
      ...env,
    });
    if (!allowed) {
      muteOverseerForStaff(); // before init: keeps the SDK from ever starting; after: tags + silences the session
      removeOverseerClicks(); // …and a session that turned into staff mid-page has no click listener left at all
      return;
    }
    identifyOverseer(auth.user); // first, so the SDK's init-time identify() already knows the user
    // Clean click capture (O6) starts once the SDK is running — never before, so a visitor who is gated out, muted or whose
    // init failed has no listener at all. Idempotent: syncing again for the same state installs nothing twice.
    void initOverseer().then((running) => { if (running) installOverseerClicks(); }, () => { /* initOverseer never rejects */ });
  } catch {
    // Analytics must never take the app down.
  }
}

/**
 * A signup that does NOT log in — the Try-free dialog, which waits for email confirmation — leaves the auth state
 * signed-out, so syncOverseer never learns who this tab now is. Plan O2: identify straight away with the userId from the
 * register response, so the rest of the visit joins the new customer. The same gate decides (a QA email, `?qa=1`, a
 * bot: the session is muted instead). Fail-silent.
 */
export function identifyOverseerSignup(userId: unknown, email?: string, env: GateEnvironment = readGateEnvironment()): void {
  try {
    if (typeof userId !== 'string' || !userId) return;
    if (!shouldInitOverseer({ user: null, impersonating: false, email, ...env })) {
      muteOverseerForStaff();
      return;
    }
    identifyOverseer({ userId });
  } catch {
    // Analytics must never take the app down.
  }
}
